import { useEffect, useRef, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get, type Ingest, type StreamKey } from '../../api'
import type { LiveModel } from '../adapter'
import { formatDuration, serverName } from '../format'
import { ObsConnection } from '../ObsConnection'
import { VerticalInput } from '../VerticalInput'
import { StreamPreview } from '../StreamPreview'
import { useObsProtocol } from '../srt'
import { t } from '../../i18n'
import { leftText } from '../slate'
import type { StageState } from './model'
import { Button, Icon } from '../../ui'

/**
 * The stage (docs/live-studio.md §1, S1): what the encoder sends, big. Before the
 * stream: what to paste into OBS with Copy (as in step 1 of the guide). OBS gone
 * during a broadcast: "OBS stopped sending" (no countdown: the user API has no hold
 * deadline yet). The guide runs inside it instead of the picture.
 * quiet: the connection is open in the Input panel, the stage only says it listens;
 * onBack (owner, 19.10): a way back to the address and key without a reload.
 */
export function Stage({ m, now, state, wizard, quiet, phone, onInput, onBack }: {
  m: LiveModel; now: number; state: StageState; wizard?: ReactNode; quiet?: boolean; phone?: boolean; onInput: () => void; onBack?: () => void;
}) {
  // back from the Input panel: the focus returns to the link that opened it
  const backed = useRef(false)
  const s = m.summary
  const dur = s.live && s.since ? formatDuration(Math.max(0, Math.floor((now - Date.parse(s.since)) / 1000))) : ''
  // The picture keeps 16:9 and fits the column under the bars (desktop: the page never scrolls).
  const box = phone ? undefined : { maxWidth: 'calc((100dvh - 330px) * 16 / 9)' }
  if (state === 'wizard') {
    return (
      <section data-testid="stage" data-state="wizard" aria-label={t('stage.label')} className="flex min-h-0 flex-col">
        {wizard}
      </section>
    )
  }
  return (
    <section data-testid="stage" data-state={state} aria-label={t('stage.label')} className="mx-auto flex w-full flex-none flex-col gap-2" style={box}>
      <div className={`relative w-full overflow-hidden rounded-lg border ${state === 'dropped' ? 'border-warn/60' : state === 'live' ? 'border-line' : 'border-dashed border-line-2'} ${state === 'prelive' && !quiet ? '' : 'aspect-video'} bg-bg`}>
        {state === 'live' && (
          m.source.preview ? (
            <StreamPreview stream={m.source.preview.stream} available={m.source.preview.available} now={now} width={640} height={360} fluid frameLabel />
          ) : (
            <div className="absolute inset-0 grid place-items-center p-4 text-center text-[13px] text-muted">{t('stage.noPicture')}</div>
          )
        )}
        {state === 'live' && (
          // a plate of the system's preview (.sx-preview .plate): a still state icon — the strip has the chip
          <span data-testid="stage-live" className="absolute left-2.5 top-2.5 flex items-center gap-2 rounded-md bg-overlay px-2 py-1 font-mono text-xs font-medium text-live backdrop-blur-[6px]">
            <Icon name="state-live" size={12} />{t('bar.live')} <span className="num text-text">{dur}</span>
          </span>
        )}
        {state === 'dropped' && !m.source.slate && <Dropped onInput={onInput} />}
        {(state === 'dropped' || state === 'live') && m.source.slate && <Slate slate={m.source.slate} now={now} />}
        {state === 'unknown' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <span className="font-mono text-xs tracking-[.12em] text-warn">? {t('stage.unknown.tag')}</span>
            <span className="max-w-[46ch] text-sm text-text-soft">{t('stage.unknown.body')}</span>
          </div>
        )}
        {state === 'prelive' && (quiet ? (
          <Listening quiet m={m} onBack={onBack && (() => { backed.current = true; onBack() })} />
        ) : (
          <PreLive m={m} phone={phone} onInput={onInput} focusMore={backed} />
        ))}
      </div>
    </section>
  )
}

function Listening({ m, quiet, onBack }: { m: LiveModel; quiet?: boolean; onBack?: () => void }) {
  return (
    <div className={`${quiet ? 'absolute inset-0' : ''} flex flex-col items-center justify-center gap-2 p-4 text-center`}>
      <div className="flex items-center gap-2 font-mono text-xs tracking-[.12em] text-muted">
        {/* still: the Waiting logo in the header is the view's one heartbeat (AUDIT WZ-04) */}
        <Icon name="state-start" size={12} className="text-start" />{t('stage.listening')}{m.source.node ? ` · ${serverName(m.source.node, false)}` : ''}
      </div>
      {quiet && <p className="m-0 text-[13px] text-text-soft">{t('stage.quiet')}</p>}
      {quiet && onBack && (
        <button type="button" data-testid="stage-back" onClick={onBack} className="min-h-9 text-xs text-accent underline-offset-2 hover:underline max-md:min-h-[44px]">
          {t('stage.back')}
        </button>
      )}
    </div>
  )
}

/** Before the stream: the address and key for OBS, with Copy (secrets only on Show / Copy, see ObsConnection). */
function PreLive({ m, phone, onInput, focusMore }: { m: LiveModel; phone?: boolean; onInput: () => void; focusMore: { current: boolean } }) {
  const more = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!focusMore.current) return
    focusMore.current = false
    more.current?.focus()
  }, [focusMore])
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest') })
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') })
  const key = keys.data?.find((k) => k.stream_name === m.source.streamName && !k.revoked_at) ?? keys.data?.find((k) => !k.revoked_at) ?? keys.data?.[0]
  const [proto, setProto] = useObsProtocol(m.source.protocol)
  return (
    <div data-testid="stage-connection" className="flex flex-col items-center gap-3 bg-[radial-gradient(ellipse_at_center,var(--panel),var(--bg))] px-4 py-5 md:px-8 md:py-7">
      <Listening m={m} />
      <h2 className="m-0 text-center text-xl font-semibold md:text-2xl">{t('stage.start')}</h2>
      {!phone && <p className="m-0 max-w-[56ch] text-center text-[13px] text-text-soft">{t('stage.obsHow')}</p>}
      <div className="flex w-full max-w-[600px] flex-col gap-3 text-left">
        <ObsConnection ingest={ingest.data} streamKey={key} proto={proto} onProto={setProto} />
        {/* VERT-1 (mockup B1 / B2): the vertical input under the main key, before the stream */}
        <VerticalInput streamName={key?.stream_name} ingest={ingest.data} />
      </div>
      <button ref={more} type="button" data-testid="stage-more" onClick={onInput} className="min-h-9 text-xs text-accent underline-offset-2 hover:underline max-md:min-h-[44px]">
        {t('stage.more')}
      </button>
    </div>
  )
}

/** The break screen is on (SLATE-1): what viewers see and how long it lasts — words, not only colour. */
function Slate({ slate, now }: { slate: { reason: string; until: string }; now: number }) {
  const left = leftText(slate.until, now)
  return (
    <div data-testid="stage-slate" role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-bg/90 p-4 text-center">
      <span className="flex items-center gap-2 font-mono text-xs font-semibold tracking-[.12em] text-warn">
        <span aria-hidden className="h-2 w-2 rounded-full bg-warn" />{t('slate.chip')}
      </span>
      <h2 className="m-0 text-lg font-semibold text-text md:text-2xl" data-testid="stage-slate-title">{t('slate.stage', { left })}</h2>
      <p className="m-0 max-w-[52ch] text-sm text-text-soft">
        {slate.reason === 'source_stalled' ? t('slate.stageStalled', { left }) : t('slate.stageLeft', { left })}
      </p>
    </div>
  )
}

function Dropped({ onInput }: { onInput: () => void }) {
  return (
    <div data-testid="stage-dropped" className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-4 text-center">
      <span className="sx-chip fail"><Icon name="state-fail" size={12} />{t('stage.dropped.tag')}</span>
      <h2 className="m-0 text-xl font-medium md:text-2xl">{t('state.dropped')}</h2>
      <p className="m-0 max-w-[48ch] text-sm text-text-soft">{t('stage.dropped.body')}</p>
      <Button size="sm" onClick={onInput} className="max-md:min-h-[44px]">{t('stage.dropped.check')}</Button>
    </div>
  )
}
